"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { MapPin, Phone, Mail, Clock, Loader, Check, X } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const contactInfo = [{
  icon: MapPin,
  title: "Werkgebied",
  details: "Den Haag, Rotterdam en omliggende gemeenten"
}, {
  icon: Phone,
  title: "Telefoonnummer",
  details: "+31 85 060 86 68"
}, {
  icon: Mail,
  title: "E-mailadres",
  details: "info@curanovazorg.nl"
}, {
  icon: Clock,
  title: "Openingstijden",
  details: "Maandag tot vrijdag: 8:00 - 18:00"
}];
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
                <div className="size-16 bg-primary/10 text-primary rounded-full flex items-center justify-center mb-2">
                    <Check className="size-8" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Bericht succesvol verzonden</h3>
                <p className="text-muted-foreground max-w-sm">{successMessage}</p>
                <Button onClick={resetForm} variant="outline" className="mt-6">
                    Nieuw bericht sturen
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit}>
            <div className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <FormField control={control} name="name" render={({
          field
        }) => <FormItem>
                                <FormLabel>Naam <span className="text-destructive">*</span></FormLabel>
                                <FormControl>
                                    <Input placeholder="Jan Jansen" {...field} className="bg-background" />
                                </FormControl>
                                <FormDescription>Vul uw volledige naam in.</FormDescription>
                                <FormMessage />
                            </FormItem>} />

                    <FormField control={control} name="email" render={({
          field
        }) => <FormItem>
                                <FormLabel>E-mailadres <span className="text-destructive">*</span></FormLabel>
                                <FormControl>
                                    <Input type="email" placeholder="jan@example.com" {...field} className="bg-background" />
                                </FormControl>
                                <FormDescription>We nemen via dit e-mailadres contact met u op.</FormDescription>
                                <FormMessage />
                            </FormItem>} />
                </div>

                <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
                            <FormLabel>Telefoonnummer</FormLabel>
                            <FormControl>
                                <Input type="tel" placeholder="+31 85 060 86 68" {...field} className="bg-background" />
                            </FormControl>
                            <FormDescription>Optioneel, voor een sneller telefonisch contact.</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <FormField control={control} name="region" render={({
          field
        }) => <FormItem>
                                <FormLabel>Werkgebied / Regio</FormLabel>
                                <Select onValueChange={field.onChange} defaultValue={field.value}>
                                    <FormControl>
                                        <SelectTrigger className="bg-background">
                                            <SelectValue placeholder="Selecteer een regio" />
                                        </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                        {["Den Haag", "Rotterdam", "Omstreken (bijv. Delft, Zoetermeer)"].map((option, i) => <SelectItem key={i} value={option} data-index={i}>{option}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                <FormDescription>Geef aan in welke regio u zorg of informatie zoekt.</FormDescription>
                                <FormMessage />
                            </FormItem>} />

                    <FormField control={control} name="subject" render={({
          field
        }) => <FormItem>
                                <FormLabel>Onderwerp <span className="text-destructive">*</span></FormLabel>
                                <Select onValueChange={field.onChange} defaultValue={field.value}>
                                    <FormControl>
                                        <SelectTrigger className="bg-background">
                                            <SelectValue placeholder="Kies een onderwerp" />
                                        </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                        {["Algemene vraag", "Zorgaanvraag", "Verwijzing / Samenwerking", "Overig"].map((option, i) => <SelectItem key={i} value={option} data-index={i}>{option}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                <FormDescription>Selecteer het type vraag of verzoek.</FormDescription>
                                <FormMessage />
                            </FormItem>} />
                </div>

                <FormField control={control} name="message" render={({
        field
      }) => <FormItem>
                            <FormLabel>Bericht <span className="text-destructive">*</span></FormLabel>
                            <FormControl>
                                <Textarea placeholder="Beschrijf uw vraag of verzoek zo gedetailleerd mogelijk." className="min-h-[120px] bg-background resize-y" {...field} />
                            </FormControl>
                            <FormDescription>We behandelen uw bericht vertrouwelijk.</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
                        <X className="size-5 text-destructive shrink-0 mt-0.5" />
                        <p className="text-sm text-destructive">{submitError}</p>
                    </div>}

                <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto mt-4">
                    {isSubmitting ? <>
                            <Loader className="animate-spin mr-2 size-4" />
                            Bericht verzenden...
                        </> : 'Verstuur bericht'}
                </Button>
            </div>
        </Form>;
}
export default function ContactEnWerkgebied() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="contact-en-werkgebied" className="bg-background py-16 md:py-24 overflow-hidden" ref={sectionRef}>
            <div className={`container mx-auto px-4 transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
                    
                    {}
                    <div className="flex flex-col gap-8">
                        <div>
                            <h2 className="text-3xl md:text-4xl font-serif text-primary mb-4">
                                Contact & Werkgebied
                            </h2>
                            <p className="text-lg text-muted-foreground leading-relaxed">
                                CuraNova Zorg is actief in de regio Haaglanden en Rijnmond, waaronder Den Haag, Rotterdam en omliggende gemeenten. Onze zorgdekking is flexibel en wordt altijd zorgvuldig afgestemd op uw specifieke zorgvraag en persoonlijke situatie.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {contactInfo.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-4 p-4 rounded-xl bg-secondary/50 border border-border/50 transition-colors hover:bg-secondary">
                                    <div className="bg-primary/10 p-2.5 rounded-lg text-primary shrink-0">
                                        <item.icon className="size-5" />
                                    </div>
                                    <div>
                                        <h4 className="font-medium text-foreground text-sm mb-1">{item.title}</h4>
                                        <p className="text-muted-foreground text-sm">{item.details}</p>
                                    </div>
                                </div>)}
                        </div>

                        <div className="rounded-2xl overflow-hidden border border-border shadow-sm bg-card relative group">
                            <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10 pointer-events-none"></div>
                            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579270/img/healthcare_generic.png" alt="CuraNova Zorg zorgverlening" className="w-full h-auto object-cover aspect-[4/3] sm:aspect-[16/9] lg:aspect-[4/3] transition-transform duration-700 group-hover:scale-105" />
                        </div>
                    </div>

                    {}
                    <div className="h-full">
                        <Card className="h-full shadow-lg border-border/60 bg-card/50 backdrop-blur-sm">
                            <CardHeader className="pb-6 border-b border-border/40">
                                <CardTitle className="text-2xl font-serif text-primary">Stuur ons een bericht</CardTitle>
                                <CardDescription className="text-base mt-2">
                                    Heeft u een vraag, wilt u advies of een kennismaking inplannen? Vul het onderstaande formulier in en wij nemen zo spoedig mogelijk contact met u op.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="pt-6">
                                <FormProvider formId="contact_form_contact" sectionName="Contact en werkgebied" defaultValues={{
                name: "",
                email: "",
                phone: "",
                region: "",
                subject: "",
                message: ""
              }} validationRules={{
                name: {
                  required: "Uw naam is verplicht"
                },
                email: {
                  required: "Uw e-mailadres is verplicht",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Vul een geldig e-mailadres in"
                  }
                },
                subject: {
                  required: "Kies a.u.b. een onderwerp"
                },
                message: {
                  required: "Een bericht is verplicht"
                }
              }} submitText="Verstuur bericht" successMessage="Hartelijk dank voor uw bericht. Wij hebben uw aanvraag in goede orde ontvangen en nemen zo spoedig mogelijk contact met u op." errorMessage="Er is helaas een fout opgetreden bij het versturen van uw bericht. Controleer uw gegevens en probeer het later opnieuw.">
                                    <ContactFormInner />
                                </FormProvider>
                            </CardContent>
                        </Card>
                    </div>

                </div>
            </div>
        </section>;
}